/* eslint-disable no-template-curly-in-string */

import React from 'react';
import PropTypes from 'prop-types';
import withData from '@bd/bdcore-data';
import { get, orderBy } from 'lodash';
import { Link } from '@bd/bdcore-named-routes';
import PageHelmet from '@bd/bdcore-pagehelmet';
import { css } from '@bd/bdui-styles/emotion';
import PageWrapper from '../../components/PageWrapper';
import InsideHeader from '../../components/InsideHeader';
import PageSectionHeading from '../../components/PageSectionHeading';
import { colors, typography } from '../../styles';
import { originalColors } from '../../styles/colors';
import SectionsGuide from './SectionsGuide';

const styles = css`
  .Code_label {
    font-size: 14px;
    margin: 10px 0 20px 0;
    &:last-child {
      margin-bottom: 60px;
    }
  }
  .Typography_sample {
    font-size: 42px;
    @media (min-width: 1201px) {
      font-size: 48px;
    }
  }
  .Typography_label {
    font-size: 14px;
    margin: 20px 0 0 0;
    padding: 20px 0 0 0;
    border-top: 1px solid ${colors.neutral};
    &:last-child {
      margin-bottom: 60px;
    }
  }
  .ColorSwatches {
    height: 80px;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-transform: capitalize;
  }
  .ColorSwatches_modified {
    width: 100%;
    height: 40px;
    display: flex;
    flex-wrap: nowrap;
  }
  .ColorSwatches_modifiedSwatch {
    height: 40px;
    flex: 1;
    flex-shrink: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

function StyleGuide({ page }) {
  return (
    <PageWrapper>
      <PageHelmet page={{ meta: { name: 'Style Guide' } }} />
      <InsideHeader
        h1="Style Guide"
        background={get(page, 'page_image.contentUrl')}
        page={page}
      />
      <div className={styles}>
        <div className="container my-5">
          <PageSectionHeading h3="Colors" />
          <div className="row">
            {
              Object.keys(originalColors).map(color => {
                const variants = Object.keys(colors).filter(key => key.indexOf(color) === 0 && !key.includes('Text'));

                return (
                  <div className="col-md-6 col-lg-4 col-xl-3 text-center">
                    <div
                      className="ColorSwatches"
                      style={{ backgroundColor: colors[color], color: colors[`${color}TextSmall`] }}
                    >
                      <span>{color.replace(/^(.*?)(\d{1,})/, '$1 $2')}</span>
                      <span>
                        {colors[color]}&nbsp;&nbsp;
                        <span className={`text-${color}TextSmall`}>S</span>
                        <span className={`text-${color}TextLarge`}>L</span>
                      </span>
                    </div>
                    <div className="ColorSwatches_modified">
                      {
                        orderBy(variants, v => colors[v]).filter(v => v !== color).map(variant => (
                          <div key={variant} className={`ColorSwatches_modifiedSwatch bg-${variant}`}>
                            <span className={`text-${variant}TextSmall`}>S</span>
                            <span className={`text-${variant}TextLarge`}>L</span>
                          </div>
                        ))
                      }
                    </div>
                    <h4 className="Code_label">
                      <strong>Variables: </strong>
                      {variants.sort().map(variant => `\${colors.${variant}}`).join(' ')}
                    </h4>
                  </div>
                );
              })
            }
          </div>
          <PageSectionHeading h3="Typography" />
          <div className="row">
            <div className="col-md-6 col-xl-3 text-center">
              <h3 className="Typography_sample text-sans-serif">Sans Serif</h3>
              <h4 className="Typography_label">
                <strong>Value: </strong>{typography.SansSerif}
              </h4>
              <h4 className="Typography_label">
                <strong>Variable: </strong>{'${typography.SansSerif}'}
              </h4>
              <h4 className="Typography_label">
                <strong>Helper Class: </strong> className={'"text-sans-serif"'}
              </h4>
            </div>
            <div className="col-md-6 col-xl-3 text-center">
              <h3 className="Typography_sample text-serif">Serif</h3>
              <h4 className="Typography_label">
                <strong>Value: </strong>{typography.Serif}
              </h4>
              <h4 className="Typography_label">
                <strong>Variable: </strong>{'${typography.Serif}'}
              </h4>
              <h4 className="Typography_label">
                <strong>Helper Class: </strong> className={'"text-serif"'}
              </h4>
            </div>
            <div className="col-md-6 col-xl-3 text-center">
              <h3 className="Typography_sample text-condensed">Condensed</h3>
              <h4 className="Typography_label">
                <strong>Value: </strong>{typography.Condensed}
              </h4>
              <h4 className="Typography_label">
                <strong>Variable: </strong>{'${typography.Condensed}'}
              </h4>
              <h4 className="Typography_label">
                <strong>Helper Class: </strong> className={'"text-condensed"'}
              </h4>
            </div>
            <div className="col-md-6 col-xl-3 text-center">
              <h3 className="Typography_sample text-display">Display</h3>
              <h4 className="Typography_label">
                <strong>Value: </strong>{typography.Display}
              </h4>
              <h4 className="Typography_label">
                <strong>Variable: </strong>{'${typography.Display}'}
              </h4>
              <h4 className="Typography_label">
                <strong>Helper Class: </strong> className={'"text-display"'}
              </h4>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <h1>Heading 1</h1>
              <h2>Heading 2</h2>
              <h3>Heading 3</h3>
              <h4>Heading 4</h4>
              <h5>Heading 5</h5>
              <h6>Heading 6</h6>
            </div>
            <div className="col-md-6">
              <h3 className="Typography_sample">Paragraph</h3>
              <p>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Etiam
                et fermentum dui. Ut orci quam, ornare sed lorem sed, hendrerit
                auctor dolor. Nulla viverra, nibh quis ultrices malesuada, ligula
                ipsum vulputate diam, aliquam egestas nibh ante vel dui. Sed in
                tellus interdum eros vulputate placerat sed non enim. Pellentesque
                eget justo porttitor urna dictum fermentum sit amet sed mauris.
                Praesent molestie vestibulum erat ac rhoncus. Aenean nunc risus,
                accumsan nec.
              </p>
            </div>
          </div>
          <PageSectionHeading h3="Buttons" />
          <div className="row">
            <div className="col-lg-4 text-center">
              <Link className="btn mx-auto">Standard Button</Link>
              <h4 className="Code_label">className={'"btn"'}</h4>
            </div>
            <div className="col-lg-4 text-center">
              <Link className="btn btn-brand1 mx-auto">Button Brand 1</Link>
              <h4 className="Code_label">className={'"btn btn-brand1"'}</h4>
            </div>
            <div className="col-lg-4 text-center">
              <Link className="btn btn-brand2 mx-auto">Button Brand 2</Link>
              <h4 className="Code_label">className={'"btn btn-brand2"'}</h4>
            </div>
            <div className="col-lg-4 text-center">
              <Link className="btn btn-brand3 mx-auto">Button Brand 3</Link>
              <h4 className="Code_label">className={'"btn btn-brand3"'}</h4>
            </div>
            <div className="col-lg-4 text-center">
              <Link className="btn btn-brand4 mx-auto">Button Brand 4</Link>
              <h4 className="Code_label">className={'"btn btn-brand4"'}</h4>
            </div>
            <div className="col-lg-4 text-center">
              <Link className="btn btn-hollow mx-auto">Button Hollow</Link>
              <h4 className="Code_label">className={'"btn btn-hollow"'}</h4>
            </div>
            <div className="col-lg-4 text-center">
              <Link className="btn btn-hollow-white mx-auto">Hollow White</Link>
              <h4 className="Code_label">className={'"btn btn-hollow-white"'}</h4>
            </div>
            <div className="col-lg-4 text-center">
              <Link className="btn btn-hollow-brand1 mx-auto">Hollow Brand 1</Link>
              <h4 className="Code_label">className={'"btn btn-hollow-brand1"'}</h4>
            </div>
            <div className="col-lg-4 text-center">
              <Link className="btn btn-hollow-brand2 mx-auto">Hollow Brand 2</Link>
              <h4 className="Code_label">className={'"btn btn-hollow-brand2"'}</h4>
            </div>
            <div className="col-lg-4 text-center">
              <Link className="btn btn-hollow-brand3 mx-auto">Hollow Brand 3</Link>
              <h4 className="Code_label">className={'"btn btn-hollow-brand3"'}</h4>
            </div>
            <div className="col-lg-4 text-center">
              <Link className="btn btn-hollow-brand4 mx-auto">Hollow Brand 4</Link>
              <h4 className="Code_label">className={'"btn btn-hollow-brand4"'}</h4>
            </div>
          </div>
          <PageSectionHeading h3="Icon Buttons" />
          <div className="row">
            <div className="col-lg-4 text-center">
              <Link className="btn btn-arrow-white mx-auto">Arrow White</Link>
              <h4 className="Code_label">className={'"btn btn-arrow-white"'}</h4>
            </div>
            <div className="col-lg-4 text-center">
              <Link className="btn btn-hollow btn-arrow-black mx-auto">Arrow Black</Link>
              <h4 className="Code_label">className={'"btn btn-hollow btn-arrow-black"'}</h4>
            </div>
            <div className="col-lg-4 text-center">
              <Link className="btn btn-phone-white mx-auto">Phone White</Link>
              <h4 className="Code_label">className={'"btn btn-phone-white"'}</h4>
            </div>
            <div className="col-lg-4 text-center">
              <Link className="btn btn-hollow btn-phone-black mx-auto">Phone Black</Link>
              <h4 className="Code_label">className={'"btn btn-hollow btn-phone-black"'}</h4>
            </div>
            <div className="col-lg-4 text-center">
              <Link className="btn btn-chat-white mx-auto">Chat White</Link>
              <h4 className="Code_label">className={'"btn btn-chat-white"'}</h4>
            </div>
            <div className="col-lg-4 text-center">
              <Link className="btn btn-hollow btn-chat-black mx-auto">Chat Black</Link>
              <h4 className="Code_label">className={'"btn btn-hollow btn-chat-black"'}</h4>
            </div>
          </div>
        </div>
      </div>
      <SectionsGuide page={page} />
    </PageWrapper>
  );
}

StyleGuide.propTypes = {
  page: PropTypes.object.isRequired,
};

export default withData({
  page: 'home_page',
})(StyleGuide);



// WEBPACK FOOTER //
// ./src/pages/StyleGuide/index.js